import React, { useEffect, useRef, useState, ReactNode } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import SAlert from '../../../components/SAlert';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from '../../../resources/helper';
import InputGroup from 'react-bootstrap/InputGroup';
import CurrencyInput from 'react-currency-input-field';
import DatePicker, { ReactDatePicker } from "react-datepicker";
import '../../../assets/css/datepicker.css'
import { TransactionAdjustmentFormI } from '../../../resources/form-props';
import { SELECT_CUSTOM_STYLE } from '../../../resources/constants';
import SelectCustomOption from '../../../components/SelectCustomOption';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreateTransactionAdjustmentValidate } from '../../../resources/form-validator';

interface CreateTransactionAdjustmentModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (data: any) => void;
    glAccounts: any[];
    filterAllAccountList: (inputValue: string) => Promise<any[]>;
    selectedCalendar: any;
    callingType?: 'transaction_adjustment' | 'openning_cash';
}


const CreateTransactionAdjustmentModal: React.FC<CreateTransactionAdjustmentModalProps> = ({
    show, onHide, onSave, glAccounts, filterAllAccountList, selectedCalendar, callingType = 'transaction_adjustment'
}) => {
    const calendarRef = useRef<(ReactDatePicker | null)[]>([]);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>();
    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd'));

    const { t } = useTranslation("components.Modal.ViewRegister.CreateTransactionAdjustmentModal");

    const { handleSubmit, control, formState: { errors }, reset, setValue, watch, trigger } = useForm<TransactionAdjustmentFormI>({
        defaultValues: {
            transaction_adjustment: []
        },
        resolver: yupResolver(CreateTransactionAdjustmentValidate),
        mode: 'onChange',
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "transaction_adjustment", // unique name for your Field Array
    });

    const onSubmit = (data: any) => {
        setFormData(data);
        setShowAlert(true);
    };

    const processData = () => {
        setShowAlert(false);
        onSave(formData['transaction_adjustment']);
        reset();
        onHide();
    }

    const submitForm = () => {
        handleSubmit(onSubmit)();
        // setShowAlert(true);
    };

    const handleClose = () => {
        reset();
        onHide();
    };

    const openCalendar = (index: number) => {
        if (calendarRef.current && calendarRef.current[index]) {
            calendarRef.current[index]?.setOpen(true)
        }
    }

    const _handleAddMore = () => {
        append({
            description: '',
            transaction_at: new Date(selectedCalendar.year, selectedCalendar.month - 1, selectedCalendar.day ?? 1),
            adjustment_debit: 0,
            adjustment_credit: 0,
            adjustment_master_debit_account_id: null,
            adjustment_master_credit_account_id: null,
        });

        setTimeout(() => {
            const lastIndex = calendarRef.current.length - 1;
            if (calendarRef.current[lastIndex]) {
                calendarRef.current[lastIndex]?.setOpen(true);
                (calendarRef.current[lastIndex] as any)?.setFocus();
            }
        }, 0);
    }

    const _handleRemove = (index: number) => {
        remove(index);
        calendarRef.current.splice(index, 1);
    }

    const handleEnterKey = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
    
            const form = event.currentTarget;
            const focusableElements = Array.from(
                form.querySelectorAll(
                    'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
                )
            ) as HTMLElement[];
    
            const index = focusableElements.indexOf(document.activeElement as HTMLElement);
            if (index > -1 && index < focusableElements.length - 1) {
                focusableElements[index + 1].focus();
                focusableElements[index + 1].dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            }
        }
    }

    const downloadAsPdf = () => {
        const element: HTMLElement | null = document.querySelector('.pdf-image');
        if (element) {
            import('html2canvas').then(html2canvas => {
                html2canvas.default(element).then(canvas => {
                    import('jspdf').then(jsPDF => {
                        const pdf = new jsPDF.default('p', 'mm', 'a4');
                        const imgData = canvas.toDataURL('image/png');
                        const imgWidth = 210; // A4 width in mm with padding (210 - 10*2)
                        const pageHeight = 297; // A4 height in mm
                        const padding = 0; // Padding for the page
                        const imgHeight = (canvas.height * imgWidth) / canvas.width;
                        let heightLeft = imgHeight;
                        let position = padding;

                        // const pageWidth = pdf.internal.pageSize.getWidth();
                        // pdf.text('Transaction Adjustment', pageWidth / 2, 5, { align: 'center', baseline: 'top' });

                        pdf.addImage(imgData, 'PNG', padding, position, imgWidth, imgHeight);
                        heightLeft -= pageHeight;

                        while (heightLeft > 0) {
                            position = heightLeft - imgHeight + padding;
                            pdf.addPage();
                            
                            pdf.addImage(imgData, 'PNG', padding, position, imgWidth, imgHeight);
                            heightLeft -= pageHeight;
                        }

                        pdf.save('Transaction_Adjustment_Form.pdf');
                    });
                });
            });
        } else {
            toast.error(t("modal.pdf.error"));
        }
    }
    
    useEffect(() => {
        _handleAddMore();
    }, [])

    return (
        <Modal show={show} onHide={handleClose} size='xl' className='modal-custom-xxl pdf-image' animation={true} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{ callingType == 'transaction_adjustment' ? t('modal.title.transaction_adjustment') : callingType == 'openning_cash' ? t('modal.title.openning_cash') : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form className="create_adjustment_modal" onSubmit={handleSubmit(onSubmit)} onKeyDown={handleEnterKey}>
                    <div className='col-sm-12 text-end mb-3'>
                        <button className='btn btn-info' type='button' onClick={_handleAddMore}>
                            {t("modal.button.add_more")} &nbsp;
                            <i className='fa fa-plus'></i>
                        </button>
                    </div>
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th>{t("modal.table.header.date")}</th>
                                <th>{t("modal.table.header.description")}</th>
                                <th>{t("modal.table.header.debit_amount")}</th>
                                <th>{t("modal.table.header.debit_gl_account")}</th>
                                <th>{t("modal.table.header.credit_amount")}</th>
                                <th>{t("modal.table.header.credit_gl_account")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                fields.map((field: any, index: number) => (
                                    <tr key={field.id}>
                                        <td className="text-start" width={"15%"}>
                                            <Form.Group className="col-sm-12 date-picker-container" style={{ position: 'relative' }}>
                                                <Controller
                                                    name={`transaction_adjustment.${index}.transaction_at`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            tabIndex={index}
                                                            ref={(el) => calendarRef.current[index] = el}
                                                            name={field.name}
                                                            selected={field.value}
                                                            onChange={field.onChange}
                                                            // showYearDropdown
                                                            // scrollableYearDropdown
                                                            // yearDropdownItemNumber={40}
                                                            className="form-control"
                                                            dateFormat="MM/dd/yyyy"
                                                            minDate={moment(`${selectedCalendar.year}/${selectedCalendar.month}/01`, "YYYY/MM/DD").toDate()}
                                                            maxDate={moment(`${selectedCalendar.year}/${selectedCalendar.month}/01`, "YYYY/MM/DD").endOf('month').toDate()}
                                                            disabled={callingType == 'openning_cash'}
                                                        />
                                                    )}
                                                />
                                                <i
                                                    className="fa fa-calendar fa-xl"
                                                    aria-hidden="true"
                                                    onClick={() => openCalendar(index)}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        cursor: 'pointer',
                                                    }}
                                                ></i>
                                            </Form.Group>
                                            {
                                                errors && errors.transaction_adjustment && errors.transaction_adjustment[index] && errors.transaction_adjustment[index]?.transaction_at && errors.transaction_adjustment[index]?.transaction_at?.message && (
                                                    <Form.Text className="d-flex text-danger">
                                                        <strong>
                                                            {errors.transaction_adjustment[index]?.transaction_at?.message as ReactNode || ''}
                                                        </strong>
                                                    </Form.Text>
                                                ) || ''
                                            }
                                        </td>
                                        <td width={"20%"}>
                                            <Form.Group className="col-sm-12">
                                                <Controller
                                                    name={`transaction_adjustment.${index}.description`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <textarea
                                                            {...field}
                                                            placeholder={t("modal.input.description.placeholder")}
                                                            className="form-control"
                                                            rows={2}
                                                            tabIndex={index + 1}
                                                        />
                                                        // <Form.Control
                                                        //     {...field}
                                                        //     type="text"
                                                        //     placeholder="Enter Description"
                                                        //     className="form-control"
                                                        // />
                                                    )}
                                                />
                                            </Form.Group>
                                            {
                                                errors && errors.transaction_adjustment && errors.transaction_adjustment[index] && errors.transaction_adjustment[index]?.description && errors.transaction_adjustment[index]?.description?.message && (
                                                    <Form.Text className="d-flex text-danger">
                                                        <strong>
                                                            {errors.transaction_adjustment[index]?.description?.message as ReactNode || ''}
                                                        </strong>
                                                    </Form.Text>
                                                ) || ''
                                            }
                                        </td>
                                        <td width={"10%"}>
                                            <Form.Group className="col-sm-12 d-flex justify-content-center align-items-center">
                                                $ &nbsp;
                                                <Controller
                                                    name={`transaction_adjustment.${index}.adjustment_debit`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <CurrencyInput
                                                            className='form-control'
                                                            placeholder="0.00"
                                                            name={field.name}
                                                            onValueChange={(value) => {
                                                                setValue(field.name, (value && +value) || 0);
                                                                trigger(`transaction_adjustment.${index}.adjustment_debit`);
                                                                trigger(`transaction_adjustment.${index}.adjustment_credit`);
                                                            }}
                                                            allowDecimals
                                                            decimalsLimit={2}
                                                            decimalSeparator="."
                                                            groupSeparator=","
                                                            disableAbbreviations
                                                            allowNegativeValue={true}
                                                            tabIndex={index + 2}
                                                        />
                                                    )}
                                                />
                                            </Form.Group>
                                            {
                                                errors && errors.transaction_adjustment && errors.transaction_adjustment[index] && errors.transaction_adjustment[index]?.adjustment_debit && errors.transaction_adjustment[index]?.adjustment_debit?.message && (
                                                    <Form.Text className="d-flex text-danger">
                                                        <strong>
                                                            {errors.transaction_adjustment[index]?.adjustment_debit?.message as ReactNode || ''}
                                                        </strong>
                                                    </Form.Text>
                                                ) || ''
                                            }
                                        </td>
                                        <td width={"20%"}>
                                            <Form.Group className="col-sm-12">
                                                <Controller
                                                    control={control}
                                                    name={`transaction_adjustment.${index}.adjustment_master_debit_account_id`}
                                                    render={({ field: { value, onChange, onBlur } }) => (
                                                        <AsyncSelect
                                                            isClearable={true}
                                                            tabIndex={index + 3}
                                                            placeholder={t("modal.input.debit_account.placeholder")}
                                                            defaultOptions={glAccounts}
                                                            loadOptions={filterAllAccountList}
                                                            onChange={(selectedOption) => {
                                                                onChange(selectedOption);
                                                            }}
                                                            className=""
                                                            menuPosition="fixed"
                                                            value={value}
                                                            defaultValue={value}
                                                            styles={selectCustomStyle}
                                                            components={{ Option: SelectCustomOption }}
                                                        />
                                                    )}
                                                />

                                            </Form.Group>
                                            {
                                                errors && errors.transaction_adjustment && errors.transaction_adjustment[index] && errors.transaction_adjustment[index]?.adjustment_master_debit_account_id && errors.transaction_adjustment[index]?.adjustment_master_debit_account_id?.message && (
                                                    <Form.Text className="d-flex text-danger">
                                                        <strong>
                                                            {errors.transaction_adjustment[index]?.adjustment_master_debit_account_id?.message as ReactNode || ''}
                                                        </strong>
                                                    </Form.Text>
                                                ) || ''
                                            }
                                        </td>
                                        <td width={"10%"}>
                                            <Form.Group className="col-sm-12 d-flex justify-content-center align-items-center">
                                                $ &nbsp;
                                                <Controller
                                                    name={`transaction_adjustment.${index}.adjustment_credit`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <CurrencyInput
                                                            tabIndex={index + 4}
                                                            className='form-control'
                                                            placeholder="0.00"
                                                            name={field.name}
                                                            onValueChange={(value) => {
                                                                setValue(field.name, (value && +value) || 0);
                                                                trigger(`transaction_adjustment.${index}.adjustment_debit`);
                                                                trigger(`transaction_adjustment.${index}.adjustment_credit`);
                                                            }}
                                                            allowDecimals
                                                            decimalsLimit={2}
                                                            decimalSeparator="."
                                                            groupSeparator=","
                                                            disableAbbreviations
                                                            allowNegativeValue={true}
                                                        />
                                                    )}
                                                />
                                            </Form.Group>
                                            {
                                                errors && errors.transaction_adjustment && errors.transaction_adjustment[index] && errors.transaction_adjustment[index]?.adjustment_credit && errors.transaction_adjustment[index]?.adjustment_credit?.message && (
                                                    <Form.Text className="d-flex text-danger">
                                                        <strong>
                                                            {errors.transaction_adjustment[index]?.adjustment_credit?.message as ReactNode || ''}
                                                        </strong>
                                                    </Form.Text>
                                                ) || ''
                                            }
                                        </td>
                                        <td width={"20%"}>
                                            <Form.Group className="col-sm-12">
                                                <Controller
                                                    control={control}
                                                    name={`transaction_adjustment.${index}.adjustment_master_credit_account_id`}
                                                    render={({ field: { value, onChange, onBlur } }) => (
                                                        <AsyncSelect
                                                            tabIndex={index + 5}
                                                            isClearable={true}
                                                            placeholder={t("modal.input.credit_account.placeholder")}
                                                            defaultOptions={glAccounts}
                                                            loadOptions={filterAllAccountList}
                                                            onChange={(selectedOption) => {
                                                                onChange(selectedOption);
                                                            }}
                                                            className=""
                                                            menuPosition="fixed"
                                                            value={value}
                                                            defaultValue={value}
                                                            styles={selectCustomStyle}
                                                            components={{ Option: SelectCustomOption }}
                                                        />
                                                    )}
                                                />

                                            </Form.Group>
                                            {
                                                errors && errors.transaction_adjustment && errors.transaction_adjustment[index] && errors.transaction_adjustment[index]?.adjustment_master_credit_account_id && errors.transaction_adjustment[index]?.adjustment_master_credit_account_id?.message && (
                                                    <Form.Text className="d-flex text-danger">
                                                        <strong>
                                                            {errors.transaction_adjustment[index]?.adjustment_master_credit_account_id?.message as ReactNode || ''}
                                                        </strong>
                                                    </Form.Text>
                                                ) || ''
                                            }

                                        </td>
                                        <td width={"5%"} className='text-center'>
                                            {
                                                fields.length > 1 && (
                                                    <i
                                                        className="fa fa-trash fa-lg text-danger"
                                                        aria-hidden="true"
                                                        onClick={() => _handleRemove(index)}
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                    ></i>
                                                )
                                            }
                                        </td>

                                    </tr>
                                ))
                            }

                        </tbody>
                        <tfoot>
                            <tr>
                                <td className='text-center'></td>
                                <td></td>
                                <td className='text-center'>
                                    <span>
                                        {
                                            currencyFormat(
                                                watch("transaction_adjustment").reduce((acc, cur) => (+acc + (cur.adjustment_debit || 0)).toFixed(2), "0.00")
                                            )
                                        }
                                    </span>
                                </td>
                                <td></td>
                                <td className='text-center'>
                                    <span>
                                        {
                                            currencyFormat(
                                                watch("transaction_adjustment").reduce((acc, cur) => (+acc + (cur.adjustment_credit || 0)).toFixed(2), "0.00")
                                            )
                                        }
                                    </span>
                                </td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="d-flex justify-content-end mt-3">
                        {
                            callingType == 'transaction_adjustment' && (
                                <Button type="button" variant="warning" className="me-2" onClick={downloadAsPdf}>{t("modal.button.review_entries")}</Button>
                            ) || <></>
                        }
                        <Button type="submit" variant="primary" className="me-2">{t("modal.button.submit")}</Button>
                        <Button type='button' variant="secondary" onClick={handleClose}>{t("modal.button.close")}</Button>
                    </div>
                </Form>
            </Modal.Body>
 
            <SAlert
                show={showAlert}
                title={ callingType == 'transaction_adjustment' ? t("modal.alert.title.transaction_adjustment") : t("modal.alert.title.openning_cash", { monthYear: moment(`${selectedCalendar['year']}-${selectedCalendar['month']}`, "YYYY-MM").startOf('day').format('MMM YYYY') }) }
                msg={ callingType == 'transaction_adjustment' ? t("modal.alert.msg.transaction_adjustment") : t("modal.alert.msg.openning_cash") }
                onConfirm={processData}
                confirmBtnText={t("modal.alert.confirm_btn")}
                cancelBtnText={t("modal.alert.cancel_btn")}
                showCancel={true}
                onCancel={() => { setShowAlert(false) }}
            />
        </Modal>
    );
};

export default CreateTransactionAdjustmentModal;